import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { buildDeepLink } from "@/lib/deepLinks";

interface IncidentRow {
  id: string;
  fingerprint: string;
  severity: string;
  type: string;
  sample_message: string | null;
  occurrence_count: number;
  is_muted: boolean;
  is_resolved: boolean;
  last_seen_at: string;
}

const PER_FP_DEBOUNCE_MS = 60_000;

/**
 * Mounted once for signed-in admins. Subscribes to admin_error_incidents and
 * raises a toast for every new or freshly-bumped, unmuted/unresolved incident.
 * Tapping the toast deep-links to the Errors tab with the incident banner.
 */
export function useAdminIncidentAlerts() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const lastShownRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    if (!user || !isAdmin) return;

    const handle = (row: IncidentRow) => {
      if (!row || row.is_muted || row.is_resolved) return;
      const now = Date.now();
      const last = lastShownRef.current.get(row.fingerprint) ?? 0;
      if (now - last < PER_FP_DEBOUNCE_MS) return;
      lastShownRef.current.set(row.fingerprint, now);

      // Refresh the unresolved-count badge as soon as anything moves
      qc.invalidateQueries({ queryKey: ["admin-unresolved-incidents"] });

      const sev = (row.severity || "error").toLowerCase();
      const title = `[${sev.toUpperCase()}] ${row.type}`;
      const desc = `${row.occurrence_count}× — ${row.sample_message ?? "(no message)"}`;
      const url = buildDeepLink("admin_error_incident", { fingerprint: row.fingerprint });

      const opts = {
        description: desc,
        action: {
          label: "View incident",
          onClick: () => navigate(url),
        },
        duration:
          sev === "critical"
            ? Infinity
            : sev === "warning"
            ? 6_000
            : 10_000,
      } as const;

      if (sev === "critical" || sev === "error") {
        toast.error(title, opts);
      } else {
        toast.warning(title, opts);
      }
    };

    const channel = supabase
      .channel("admin-error-incidents-alerts")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "admin_error_incidents" },
        (payload) => handle(payload.new as IncidentRow),
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "admin_error_incidents" },
        (payload) => {
          const next = payload.new as IncidentRow;
          const prev = payload.old as Partial<IncidentRow> | null;
          // Only alert when last_seen_at advances (a new occurrence)
          if (
            prev?.last_seen_at &&
            next.last_seen_at &&
            new Date(next.last_seen_at).getTime() <=
              new Date(prev.last_seen_at).getTime()
          ) {
            // status flip (mute/resolve) — refresh badge but no toast
            qc.invalidateQueries({ queryKey: ["admin-unresolved-incidents"] });
            return;
          }
          handle(next);
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, isAdmin, navigate, qc]);
}